<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { ApiClient } from '@/services/ApiClient';
import { useLogger } from '@nzyme/vue-ioc/useLogger.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { TransitionFade } from '@nzyme/vue-transitions/TransitionFade.jsx';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import { ChatStore } from '@sigma/client/chat/ChatStore.js';
import { useLocale } from '@sigma/client/i18n/useLocale.js';
import { ActorConnectionService } from '@sigma/client/services/ActorConnectionService.js';
import { TaskQueue } from '@sigma/client/services/TaskQueue.js';
import { ChatStartNewEndpoint } from '@sigma/patient-api/chat/ChatStartNewEndpoint.js';
import { ChatRoute } from '@sigma/patient-common/routes.js';
import { provideLayout } from '@sigma/ui/layout/Layout.jsx';
import LoaderOverlay from '@sigma/ui/LoaderOverlay.vue';

import ChatLayout from './ChatLayout.vue';
import { ChatListStore } from './ChatListStore.js';
import ChatNotFound from './ChatNotFound.vue';

const route = useRoute();
const router = useRouter();
const locale = useLocale();
const apiClient = useService(ApiClient);
const actorClient = useService(ActorClient);
const actorConnection = useService(ActorConnectionService);
const taskQueue = useService(TaskQueue);
const chatListStore = useService(ChatListStore);
const chatStore = useService(ChatStore);
const logger = useLogger('ChatStartView');

provideLayout(ChatLayout);

const failed = ref(false);

function getRouteParam(name: string): string | undefined {
  const value = route.params[name];
  if (typeof value === 'string' && value) {
    return value;
  }
  return undefined;
}

onMounted(async () => {
  const agentUid = getRouteParam('agentUid');
  if (!agentUid) {
    failed.value = true;
    return;
  }

  try {
    await actorConnection.connect();
    await taskQueue.waitForAll();

    // Capture the baseline before the create request: personal-agent creation can emit a STAFF_JOIN
    // lifecycle event before the response returns, and the later ping can emit the first agent
    // message. Both are produced while the patient is already present, so recording openedAt here
    // (not at ChatView mount) keeps them witnessed-live and avoids reviving the phantom divider.
    const openedAt = Date.now();
    const result = await apiClient(ChatStartNewEndpoint, {
      lang: navigator.language,
      agentUid,
    });

    chatStore.markOpening(result.chatId, openedAt);

    chatListStore.pushChat({
      id: result.chatId,
      timestamp: Date.now(),
      state: {
        status: 'ACTIVE',
        lastEvent: undefined,
      },
      hasMore: false,
      staffId: null,
      staffFirstName: null,
      staffLastName: null,
    });

    void actorClient.actor(ChatActor).requests.ping(result.chatId);

    await router.replace(ChatRoute({ locale: locale.value, chatId: result.chatId }));
  } catch (error) {
    logger.error('Failed to start chat with agent', { agentUid, error });
    failed.value = true;
  }
});
</script>

<template>
  <main :class="css.page">
    <TransitionFade>
      <LoaderOverlay
        v-if="!failed"
        :class="css.loaderOverlay"
      />
    </TransitionFade>

    <ChatNotFound v-if="failed" />
  </main>
</template>

<style lang="scss" module="css">
.page {
  display: flex;
  flex-direction: column;
  padding-top: $main-header-height;
  flex: 1;
}

.loaderOverlay {
  background: $color-doctor-50;
  position: fixed;
}
</style>
